import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  changeCurrentUserPassword,
  updateCurrentUserProfile,
} from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { PasswordRequirementsChecklist } from "./PasswordRequirementsChecklist";
import { describeMissingRequirements, validatePassword } from "../utils/passwordPolicy";

type UserSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const UserSettingsDialog: React.FC<UserSettingsDialogProps> = ({
  open,
  onClose,
}) => {
  const { user, refreshUser } = useAuth();
  const [firstname, setFirstname] = React.useState("");
  const [lastname, setLastname] = React.useState("");
  const [changePassword, setChangePassword] = React.useState(false);
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setFirstname(user?.firstname || "");
    setLastname(user?.lastname || "");
    setChangePassword(false);
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordConfirm("");
  }, [open, user]);

  const handleDialogClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSaveSettings = async () => {
    if (changePassword) {
      if (!currentPassword || !newPassword || !newPasswordConfirm) {
        toast.error("Compila tutti i campi password");
        return;
      }
      if (newPassword !== newPasswordConfirm) {
        toast.error("Le nuove password non coincidono");
        return;
      }
      const missingRequirements = describeMissingRequirements(newPassword);
      if (missingRequirements) {
        toast.error(missingRequirements);
        return;
      }
    }

    setLoading(true);
    try {
      await updateCurrentUserProfile({
        firstname: firstname.trim(),
        lastname: lastname.trim(),
      });

      if (changePassword) {
        await changeCurrentUserPassword({
          current_password: currentPassword,
          new_password: newPassword,
          new_password_confirm: newPasswordConfirm,
        });
      }

      await refreshUser();
      toast.success("Profilo aggiornato con successo");
      handleDialogClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.detail || "Errore nell'aggiornamento del profilo"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle>Impostazioni Profilo</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Nome"
            value={firstname}
            onChange={(e) => setFirstname(e.target.value)}
            fullWidth
          />
          <TextField
            label="Cognome"
            value={lastname}
            onChange={(e) => setLastname(e.target.value)}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={changePassword}
                onChange={(e) => setChangePassword(e.target.checked)}
              />
            }
            label="Cambia password"
          />
          {changePassword && (
            <>
              <TextField
                label="Password attuale"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
              />
              <TextField
                label="Nuova password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
              />
              {newPassword.length > 0 && (
                <PasswordRequirementsChecklist password={newPassword} />
              )}
              <TextField
                label="Conferma nuova password"
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                fullWidth
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box>
          <Button onClick={handleDialogClose} disabled={loading}>
            Annulla
          </Button>
          <Button
            sx={{ ml: 2 }}
            onClick={handleSaveSettings}
            variant="contained"
            disabled={
              loading ||
              (changePassword &&
                (!validatePassword(newPassword).valid ||
                  newPassword !== newPasswordConfirm))
            }
          >
            {loading ? <CircularProgress size={24} /> : "Salva"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
